"use client"

import { Dialog as BaseDialog } from "@base-ui/react/dialog"
import Button from "@src/components/Button"
import { NearIntentsLogoSymbolIcon } from "@src/icons"
import { AnimatePresence, motion } from "framer-motion"
import { useCallback, useEffect, useState } from "react"
import {
  type WelcomeOutcome,
  getPendingWelcome,
  markWelcomeSeen,
  onReferralStateChanged,
} from "../lib/referralStorage"
import {
  trackReferralWelcomeDialogConfirmed,
  trackReferralWelcomeDialogDismissed,
  trackReferralWelcomeDialogShown,
} from "../lib/referralTracking"

const circles = {
  animate: {
    transition: {
      delayChildren: 0.1,
      staggerChildren: 0.2,
    },
  },
}

const circle = {
  initial: { opacity: 0, scale: 0 },
  animate: (opacity: number) => ({
    opacity,
    scale: 1,
    transition: {
      duration: 0.7,
      type: "spring",
      bounce: 0.3,
    },
  }),
}

const COPY: Record<
  WelcomeOutcome,
  { title: string; description: string; cta: string; celebratory: boolean }
> = {
  success: {
    title: "Referral connected",
    description:
      "You and your friend will both earn when you trade on near.com.",
    cta: "Start trading",
    celebratory: true,
  },
  not_claimable: {
    title: "Referral didn't apply",
    description: "We couldn't link the referral to your wallet.",
    cta: "Continue",
    celebratory: false,
  },
}

export function ReferralWelcomeDialog() {
  const [outcome, setOutcome] = useState<WelcomeOutcome | null>(null)

  useEffect(() => {
    const check = () => {
      const next = getPendingWelcome()
      if (next) setOutcome(next)
    }
    check()
    return onReferralStateChanged(check)
  }, [])

  const markSeenAndClose = useCallback(() => {
    setOutcome(null)
    markWelcomeSeen()
  }, [])

  const handleDismiss = useCallback(() => {
    if (outcome) trackReferralWelcomeDialogDismissed(outcome)
    markSeenAndClose()
  }, [outcome, markSeenAndClose])

  const handleConfirm = useCallback(() => {
    if (outcome) trackReferralWelcomeDialogConfirmed(outcome)
    markSeenAndClose()
  }, [outcome, markSeenAndClose])

  const open = outcome !== null
  const copy = outcome ? COPY[outcome] : null

  useEffect(() => {
    if (!outcome) return
    trackReferralWelcomeDialogShown(outcome)
  }, [outcome])

  return (
    <AnimatePresence>
      {open && copy && (
        <BaseDialog.Root
          open={open}
          onOpenChange={(o) => !o && handleDismiss()}
        >
          <BaseDialog.Portal>
            <BaseDialog.Backdrop className="fixed inset-0 z-10 bg-gray-900/80 transition-opacity duration-200 ease-out data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in dark:bg-gray-900/90" />

            <div className="fixed inset-0 z-20 w-screen">
              <div className="flex min-h-full items-end justify-center p-2 pb-safe-offset-2 text-center sm:items-center sm:p-0 has-sidebar:lg:pl-74 [@media(min-width:640px)_and_(min-height:850px)]:items-start [@media(min-width:640px)_and_(min-height:850px)]:pt-[10dvh]">
                <BaseDialog.Popup className="dark:-outline-offset-1 relative flex w-full max-w-sm transform flex-col overflow-hidden rounded-3xl bg-white text-left shadow-xl transition-all duration-300 ease-out data-ending-style:translate-y-4 data-starting-style:translate-y-4 data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:duration-200 data-ending-style:ease-in sm:my-8 sm:data-ending-style:translate-y-0 sm:data-starting-style:translate-y-0 sm:data-ending-style:scale-95 sm:data-starting-style:scale-95 dark:bg-gray-800 dark:outline dark:outline-white/5">
                  <div className="relative flex h-52 items-center justify-center overflow-hidden bg-green-950">
                    {copy.celebratory && (
                      <motion.div
                        variants={circles}
                        initial="initial"
                        animate="animate"
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <motion.div
                          variants={circle}
                          custom={0.15}
                          className="absolute size-40 rounded-full border-2 border-white"
                        />
                        <motion.div
                          variants={circle}
                          custom={0.08}
                          className="absolute size-64 rounded-full border-2 border-white"
                        />
                        <motion.div
                          variants={circle}
                          custom={0.05}
                          className="absolute size-88 rounded-full border-2 border-white"
                        />
                      </motion.div>
                    )}

                    {/* Logo */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.7,
                        type: "spring",
                        bounce: 0.5,
                      }}
                    >
                      <NearIntentsLogoSymbolIcon className="size-14" />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col items-center p-6">
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.25 }}
                      className="flex flex-col items-center"
                    >
                      <BaseDialog.Title className="text-balance text-center font-semibold text-gray-900 text-xl/6 tracking-tight dark:text-white">
                        {copy.title}
                      </BaseDialog.Title>

                      <BaseDialog.Description className="mt-3 text-balance text-center font-medium text-base/5 text-gray-500 dark:text-gray-400">
                        {copy.description}
                      </BaseDialog.Description>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.35 }}
                      className="mt-6 w-full"
                    >
                      <Button size="xl" fullWidth onClick={handleConfirm}>
                        {copy.cta}
                      </Button>
                    </motion.div>
                  </div>
                </BaseDialog.Popup>
              </div>
            </div>
          </BaseDialog.Portal>
        </BaseDialog.Root>
      )}
    </AnimatePresence>
  )
}
